import React from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useAdminShell } from '../shell/AdminShell';
import { RangePicker } from './RangePicker';
import type { SummaryState } from './useSummary';

/**
 * Frame shared by the Dashboard and Analytics pages: full width on the header's gutter, the date
 * range in the header slot, a retry banner when the summary fails, and the bento grid (`layout`
 * picks the grid areas in admin-theme.css).
 */
export const DashPage: React.FC<{ title: string; s: SummaryState; layout: 'home' | 'analytics'; children: React.ReactNode }> = ({ title, s, layout, children }) => {
  const { actionsEl } = useAdminShell();
  return (
    <div className="w-full px-3 pb-10 pt-3 md:px-5 md:pt-4">
      <h1 className="sr-only">{title}</h1>
      <RangePicker host={actionsEl} value={s.range} today={s.today} onApply={s.setRange} />

      {s.error ? (
        <div role="alert" className="oa-card mb-4 items-center justify-between gap-3 !py-3.5" style={{ ['--i' as string]: 0, flexDirection: 'row' } as React.CSSProperties}>
          <span className="text-[13px] text-[var(--oa-muted)]">
            <span className="font-semibold text-[var(--oa-bad)]">{title} data did not load.</span> {s.error}
          </span>
          <button type="button" className="oa-apply inline-flex items-center gap-2" onClick={s.reload}>
            <RefreshCw className={cn('h-3.5 w-3.5', s.loading && 'animate-spin')} /> Retry
          </button>
        </div>
      ) : null}

      <div className={cn('oa-bento transition-opacity duration-300', `is-${layout}`, s.stale && 'opacity-60')} aria-busy={s.loading}>
        {children}
      </div>
    </div>
  );
};
